/**
 * Voice Channel Join Node - Player View
 *
 * Premium voice lobby gathering experience.
 * Central pulsing microphone icon, radial progress ring,
 * responsive player grid with join status indicators.
 * Adapts for 2-player duels up to 100-player lobbies.
 */

import { AnimatePresence, motion } from "framer-motion";
import { Info, Mic, Volume2 } from "lucide-react";
import { useState } from "react";
import { cn } from "../../../lib/utils";
import { ErrorBanner } from "../../components/ErrorBanner";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import type { NodePlayerViewProps } from "../types";

type VoiceChannelJoinState = {
  channelId: string | null;
  channelName: string;
  joinedPlayers: string[];
  failedPlayers: string[];
  noDiscord?: boolean;
};

// Small "X / Y in voice" label used inside the ring
function CountLabel({
  joined,
  total,
  accent = false,
}: {
  joined: number;
  total: number;
  accent?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-1 font-display tabular-nums">
      <span className={cn("text-base font-bold", accent ? "text-brand-400" : "text-text-primary")}>
        {joined}
      </span>
      <span className="text-surface-500 text-xs">/</span>
      <span className="text-base font-bold text-text-primary">{total}</span>
      <span className="text-[10px] uppercase tracking-wider text-text-secondary ml-1">
        in voice
      </span>
    </div>
  );
}

// SVG arc helpers
function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
}

export function VoiceChannelJoinView({
  nodeState,
  players,
  onAction,
}: NodePlayerViewProps<VoiceChannelJoinState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = nodeState.state as VoiceChannelJoinState;
  const config = nodeState.config as { labels?: { title?: string; subtitle?: string } };
  const title = config.labels?.title ?? "Voice Channel";
  const subtitle = config.labels?.subtitle ?? "Gathering players in voice";
  const hasJoined = state.joinedPlayers.includes(userId || "");
  const hasFailed = state.failedPlayers?.includes(userId || "") ?? false;
  const joinedCount = state.joinedPlayers.length;
  const totalCount = players.length;
  const progress = totalCount > 0 ? joinedCount / totalCount : 0;
  const allJoined = totalCount > 0 && joinedCount === totalCount;

  // Radial progress arc
  const arcAngle = progress * 359.99;
  const showTicks = totalCount <= 20;

  const handleConfirmJoined = async () => {
    setIsSubmitting(true);
    setError(null);
    const result = await onAction("confirm_joined", { userId });
    if (!result.success && result.error) {
      setError(result.error);
    }
    setIsSubmitting(false);
  };

  return (
    <NodeCard>
      <NodeHeader title={title} subtitle={subtitle} />

      {/* Channel name */}
      {state.channelName && (
        <div className="flex justify-center mb-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500/10 border border-brand-500/25 shadow-[0_0_16px_rgba(38,217,181,0.08)]">
            <Volume2 size={14} className="text-brand-400 shrink-0" aria-hidden="true" />
            <span className="text-sm font-display font-semibold text-brand-300 tracking-wide">
              {state.channelName}
            </span>
          </div>
        </div>
      )}

      {/* All joined indicator */}
      {allJoined && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="flex justify-center mb-4"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500/10 border border-brand-500/20">
            <div className="w-1.5 h-1.5 rounded-full bg-brand-400" />
            <span className="text-sm font-medium text-brand-400">All players connected</span>
          </div>
        </motion.div>
      )}

      {/* noDiscord — voice unavailable, skip the full UI */}
      {state.noDiscord ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="flex justify-center"
        >
          <div className="inline-flex items-center gap-2.5 px-5 py-3 rounded-lg bg-surface-800/80 border border-surface-700/50">
            <Info size={16} className="text-surface-500 shrink-0" aria-hidden="true" />
            <span className="text-sm text-text-secondary">
              Voice chat is not configured for this server
            </span>
          </div>
        </motion.div>
      ) : (
        <>
          {/* Center section: radial ring + mic icon / confirmation */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex justify-center"
          >
            <div className="relative">
              {/* Radial progress ring */}
              <svg
                width="200"
                height="200"
                viewBox="0 0 200 200"
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                aria-hidden="true"
              >
                {/* Background ring */}
                <circle
                  cx="100"
                  cy="100"
                  r="94"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-surface-700/50"
                />
                {/* Progress arc */}
                {arcAngle > 0 && (
                  <motion.path
                    d={describeArc(100, 100, 94, 0, arcAngle)}
                    fill="none"
                    stroke="url(#voiceJoinGradient)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                )}
                {/* Segment ticks */}
                {showTicks &&
                  players.map((_, i) => {
                    const angle = (i / totalCount) * 360 - 90;
                    const rad = (angle * Math.PI) / 180;
                    const innerR = 86;
                    const outerR = 90;
                    const x1 = 100 + innerR * Math.cos(rad);
                    const y1 = 100 + innerR * Math.sin(rad);
                    const x2 = 100 + outerR * Math.cos(rad);
                    const y2 = 100 + outerR * Math.sin(rad);
                    return (
                      <line
                        key={i}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke="currentColor"
                        strokeWidth="1.5"
                        className="text-surface-600/60"
                      />
                    );
                  })}
                <defs>
                  <linearGradient id="voiceJoinGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#26d9b5" />
                    <stop offset="100%" stopColor="#3de0c0" />
                  </linearGradient>
                </defs>
              </svg>

              {/* Center content */}
              <div className="relative w-[200px] h-[200px] flex items-center justify-center">
                <AnimatePresence mode="wait">
                  {!hasJoined && !hasFailed ? (
                    <motion.div
                      key="join-btn"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.3 }}
                      className="flex flex-col items-center gap-2.5"
                    >
                      {/* Pulse glow */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-20 h-20 rounded-full bg-brand-500/20 animate-pulse-glow" />
                      </div>
                      <button
                        type="button"
                        onClick={handleConfirmJoined}
                        disabled={isSubmitting}
                        className={cn(
                          "relative z-10 w-[94px] h-[94px] rounded-full",
                          "bg-gradient-to-b from-brand-400 to-brand-600",
                          "text-surface-900 font-display font-bold text-xs uppercase tracking-wider",
                          "shadow-[0_0_30px_rgba(38,217,181,0.3),0_0_60px_rgba(38,217,181,0.1)]",
                          "hover:shadow-[0_0_40px_rgba(38,217,181,0.5),0_0_80px_rgba(38,217,181,0.2)]",
                          "hover:scale-105 active:scale-95",
                          "transition-all duration-200 cursor-pointer",
                          "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
                          "focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 focus:ring-offset-surface-900",
                          "flex flex-col items-center justify-center gap-1",
                        )}
                      >
                        {isSubmitting ? (
                          <span className="inline-block w-5 h-5 border-2 border-surface-900/30 border-t-surface-900 rounded-full animate-spin" />
                        ) : (
                          <>
                            <Mic size={24} className="text-surface-900" aria-hidden="true" />
                            <span className="leading-none">Join</span>
                          </>
                        )}
                      </button>
                      <CountLabel joined={joinedCount} total={totalCount} />
                    </motion.div>
                  ) : hasJoined ? (
                    <motion.div
                      key="joined-confirmed"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      className="flex flex-col items-center gap-2"
                    >
                      <div className="w-[64px] h-[64px] rounded-full bg-brand-500/10 border-2 border-brand-500/40 flex items-center justify-center">
                        <svg
                          width="28"
                          height="28"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="text-brand-400"
                          aria-hidden="true"
                        >
                          <motion.path
                            d="M5 13l4 4L19 7"
                            stroke="currentColor"
                            strokeWidth={2.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.4, delay: 0.2 }}
                          />
                        </svg>
                      </div>
                      <span className="text-xs font-display uppercase tracking-widest text-brand-400/80">
                        In Voice
                      </span>
                      <CountLabel joined={joinedCount} total={totalCount} accent />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="join-failed"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      className="flex flex-col items-center gap-2"
                    >
                      <div className="w-[64px] h-[64px] rounded-full bg-amber-500/10 border-2 border-amber-500/40 flex items-center justify-center">
                        <Info size={26} className="text-amber-400" aria-hidden="true" />
                      </div>
                      <span className="text-xs font-display uppercase tracking-widest text-amber-400/80">
                        No Discord
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          <ErrorBanner error={error} />
        </>
      )}
    </NodeCard>
  );
}
